import React, { useState, useMemo } from "react";
import {
  IconX,
  IconSearch,
  IconCheck,
  IconChevronLeft,
  IconTrendingUp,
  IconTrendingDown,
} from "@tabler/icons-react";
import { getAllBudgetOptions, determineType } from "_helpers/budget";
import "./AddItemModal.scss";

function AddItemModal({ open, onClose, onAdd, existingItems = [] }) {
  const [step, setStep] = useState(1); // 1 = select items, 2 = enter amounts
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedItems, setSelectedItems] = useState([]);
  const [itemValues, setItemValues] = useState({});

  // Get available options (excluding existing items)
  const availableOptions = useMemo(() => {
    return getAllBudgetOptions().filter(
      (opt) => !existingItems.includes(opt.name)
    );
  }, [existingItems]);

  // Filter options by search
  const filteredOptions = useMemo(() => {
    if (!searchQuery) return availableOptions;
    const query = searchQuery.toLowerCase();
    return availableOptions.filter(
      (opt) =>
        opt.name.toLowerCase().includes(query) ||
        opt.category.toLowerCase().includes(query)
    );
  }, [availableOptions, searchQuery]);

  // Toggle item selection
  const toggleItem = (item) => {
    const isSelected = selectedItems.find((s) => s.name === item.name);
    if (isSelected) {
      setSelectedItems(selectedItems.filter((s) => s.name !== item.name));
    } else {
      setSelectedItems([...selectedItems, item]);
    }
  };

  // Check if item is selected
  const isSelected = (item) => {
    return selectedItems.find((s) => s.name === item.name);
  };

  // Handle next step
  const handleNextStep = () => {
    if (selectedItems.length === 0) return;
    // Initialize values
    const initialValues = {};
    selectedItems.forEach((item) => {
      initialValues[item.name] = 0;
    });
    setItemValues(initialValues);
    setStep(2);
  };

  // Handle value change
  const handleValueChange = (name, value) => {
    setItemValues({ ...itemValues, [name]: parseFloat(value) || 0 });
  };

  // Handle final add
  const handleAdd = () => {
    const itemsToAdd = selectedItems.map((item) => ({
      ...determineType(item.name),
      value: itemValues[item.name] || 0,
    }));
    onAdd(itemsToAdd);
    handleClose();
  };

  // Handle close and reset
  const handleClose = () => {
    setStep(1);
    setSearchQuery("");
    setSelectedItems([]);
    setItemValues({});
    onClose();
  };

  // Handle back
  const handleBack = () => {
    setStep(1);
  };

  if (!open) return null;

  return (
    <div className="add-item-modal-overlay" onClick={handleClose}>
      <div className="add-item-modal" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="modal-header">
          <h3 className="modal-title">
            {step === 1 ? "Select all that apply" : "Enter amounts"}
          </h3>
          <button className="close-btn" onClick={handleClose}>
            <IconX size={20} />
          </button>
        </div>

        {/* Step 1: Select Items */}
        {step === 1 && (
          <>
            {/* Search */}
            <div className="search-container">
              <div className="search-input-wrapper">
                <IconSearch size={16} className="search-icon" />
                <input
                  type="text"
                  className="search-input"
                  placeholder="Search income & expenses..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
            </div>

            {/* Options List */}
            <div className="options-list">
              {filteredOptions.map((option) => (
                <div
                  key={option.name}
                  className={`option-item ${
                    isSelected(option) ? "selected" : ""
                  } ${option.type}-type`}
                  onClick={() => toggleItem(option)}
                >
                  <div className="option-info">
                    <div
                      className={`type-icon ${option.type}`}
                    >
                      {option.type === "income" ? (
                        <IconTrendingUp size={16} />
                      ) : (
                        <IconTrendingDown size={16} />
                      )}
                    </div>
                    <span className="option-name">{option.name}</span>
                  </div>
                  {isSelected(option) ? (
                    <div className={`selected-badge ${option.type}`}>
                      <IconCheck size={14} />
                      <span>Selected</span>
                    </div>
                  ) : (
                    <span className="option-category">{option.category}</span>
                  )}
                </div>
              ))}
              {filteredOptions.length === 0 && (
                <div className="empty-options">No options found</div>
              )}
            </div>

            {/* Footer */}
            <div className="modal-footer">
              <button
                className="primary-btn"
                onClick={handleNextStep}
                disabled={selectedItems.length === 0}
              >
                <span>Next Step</span>
                {selectedItems.length > 0 && (
                  <span className="count">({selectedItems.length})</span>
                )}
              </button>
            </div>
          </>
        )}

        {/* Step 2: Enter Amounts */}
        {step === 2 && (
          <>
            {/* Items with Input */}
            <div className="amounts-list">
              {selectedItems.map((item) => (
                <div key={item.name} className="amount-item">
                  <div className="amount-info">
                    <div className={`type-icon ${item.type}`}>
                      {item.type === "income" ? (
                        <IconTrendingUp size={16} />
                      ) : (
                        <IconTrendingDown size={16} />
                      )}
                    </div>
                    <span className="amount-name">{item.name}</span>
                  </div>
                  <div className="amount-input-wrapper">
                    <span className="currency-prefix">$</span>
                    <input
                      type="number"
                      className="amount-input"
                      placeholder="0"
                      value={itemValues[item.name] || ""}
                      onChange={(e) =>
                        handleValueChange(item.name, e.target.value)
                      }
                    />
                  </div>
                </div>
              ))}
            </div>

            {/* Footer */}
            <div className="modal-footer two-buttons">
              <button className="secondary-btn" onClick={handleBack}>
                <IconChevronLeft size={16} />
                <span>Back</span>
              </button>
              <button className="primary-btn" onClick={handleAdd}>
                <span>
                  Add to{" "}
                  {selectedItems[0]?.type === "income" ? "Income" : "Budget"}
                </span>
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default AddItemModal;






